import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import FinanceHubHeader from './components/FinanceHubHeader';
import TransactionsTab from './tabs/TransactionsTab';
import DashboardTab from './tabs/DashboardTab';
import InsightsTab from './tabs/InsightsTab';

type TabId = 'transactions' | 'dashboard' | 'insights';

interface Tab {
  id: TabId;
  label: string;
  icon: string;
  component: React.FC;
}

const TABS: Tab[] = [
  { id: 'transactions', label: 'Transactions', icon: '💰', component: TransactionsTab },
  { id: 'dashboard', label: 'Dashboard', icon: '📊', component: DashboardTab },
  { id: 'insights', label: 'Insights', icon: '🔍', component: InsightsTab }
];

const FinanceHub: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [activeTab, setActiveTab] = useState<TabId>('transactions');

  // Set document title
  useEffect(() => {
    document.title = 'Finance Hub | Explanatum';
  }, []);

  // Redirect if not logged in
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Handle hash-based navigation
  useEffect(() => {
    const hash = location.hash.replace('#', '') as TabId;
    if (hash && TABS.some(t => t.id === hash)) {
      setActiveTab(hash);
    }
  }, [location.hash]);

  // Update URL hash when tab changes
  const handleTabChange = (tabId: TabId) => {
    setActiveTab(tabId);
    navigate(`#${tabId}`, { replace: true });
  };

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  const ActiveTabComponent = TABS.find(t => t.id === activeTab)?.component || TransactionsTab;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Header */}
        <FinanceHubHeader />

        {/* Tab Navigation */}
        <div className="mt-8 border-b border-gray-200 bg-white">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`
                  flex items-center gap-2 whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium transition-colors
                  ${activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }
                `}
              >
                <span className="text-lg">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          <ActiveTabComponent />
        </div>
      </div>
    </div>
  );
};

export default FinanceHub;
